import { Clipboard } from '@angular/cdk/clipboard';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Animations } from 'src/app/common/utilities/animations';
import { UploadStyles } from 'src/app/private/shared/components/file-management/file-upload/file-upload.component';
import { UploadType } from 'src/app/private/shared/enums/upload-type.enum';
import { FileDTO } from '../../dtos/file.dto';
import {
  AIUserFeedback,
  ChatMessage,
  Conversation,
  GlaiUserFeedbackPayload,
} from '../../dtos/glai.dto';
import { GlaiService } from '../../services/glai/glai.service';
import {
  GlaiPreviousChatsModalComponent,
  SelectedConversationPayload,
} from './glai-previous-chats-modal/glai-previous-chats-modal.component';

@Component({
  selector: 'app-glai',
  templateUrl: './glai.component.html',
  styleUrl: './glai.component.scss',
  animations: [Animations.delayfadeInOut, Animations.fadeInOut],
  standalone: false,
})
export class GlaiComponent implements OnInit, OnDestroy {
  @ViewChild('messagesArea') private messagesArea!: ElementRef;

  @ViewChild('uploadComponent') uploadComponent: ElementRef;

  @ViewChild('textareaprompt') textareaprompt: ElementRef<HTMLTextAreaElement>;

  @ViewChild('previousChatsModal')
  previousChatsModal: GlaiPreviousChatsModalComponent;

  feedbackByMessageId: Record<string, AIUserFeedback> = {};

  @Output() readonly fileUploads = new EventEmitter<FileDTO>();

  uploadedFiles: FileDTO[] = [];

  uploadStyle = UploadStyles.ICON;

  modalUploadStyle = UploadStyles.SIMPLE;

  uploadType = UploadType.USER_UPLOAD;

  activeModes: string[] = ['default'];

  userMessage = '';

  messages: ChatMessage[] = [];

  currentConversation: Conversation | null = null;

  isLoading = false;

  error: string | null = null;

  selectedPath = 'Question & Answer';

  customCollapsed = false;

  AIUserFeedback = AIUserFeedback;

  fullscreen = false;

  currentClass = 'bi-chat-fill';

  subscriptions = new Subscription();

  @HostListener('document:keydown.escape', []) onKeydownHandler() {
    if (this.fullscreen) {
      this.toggleFullscreen();
    }
  }

  constructor(private glaiService: GlaiService, private clipboard: Clipboard) {}

  ngOnInit(): void {
    this.subscriptions.add(
      this.glaiService.messages$.subscribe((message: ChatMessage) => {
        const existingIndex = this.messages.findIndex(
          (m) => m.id === message.id
        );
        if (existingIndex >= 0) {
          this.messages[existingIndex] = message;
        } else {
          this.messages.push(message);
        }
        if (message.role === 'assistant') {
          this.isLoading = false;
        }
        setTimeout(() => this.scrollToBottom(), 300);
      })
    );
  }

  toggleFullscreen() {
    this.fullscreen = !this.fullscreen;
    if (this.fullscreen) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    }
  }

  resetConversation() {
    this.glaiService.disconnectStream();
    this.messages = [];
    this.currentConversation = null;
    this.error = null;
  }

  toggleMode(mode: string): void {
    switch (mode) {
      case 'default':
        this.selectedPath = 'Ask Anything (General)';
        this.currentClass = 'bi-chat-fill';
        break;

      case 'lesson_and_differentiation':
        this.selectedPath = 'Lessons & Differentiation';
        this.currentClass = 'bi-book-fill';
        break;

      case 'grading_and_feedback':
        this.selectedPath = 'Grading and Feedback';
        this.currentClass = 'bi-clipboard-check-fill';
        break;

      case 'communication':
        this.selectedPath = 'Professional Communication';
        this.currentClass = 'bi-envelope-fill';
        break;

      case 'classroom_management':
        this.selectedPath = 'Classroom Management';
        this.currentClass = 'bi-person-fill-gear';
        break;

      case 'leadership':
        this.selectedPath = 'Leadership';
        this.currentClass = 'bi-diagram-2';
        break;

      case 'data_analysis':
        this.selectedPath = 'Data Analysis';
        this.currentClass = 'bi-bar-chart-fill';
        break;

      default:
        this.selectedPath = 'Ask Anything (General)';
        this.currentClass = 'bi-chat-fill';
        break;
    }

    this.activeModes = [mode];
  }

  sendMessage(): void {
    if (!this.userMessage.trim() || this.isLoading) return;

    const message = this.userMessage.trim();
    this.userMessage = '';
    setTimeout(() => this.resizeInput(), 300);
    this.isLoading = true;

    // Add user message to the chat
    const messageFiles = [...this.uploadedFiles];
    this.glaiService.addUserMessage(message, messageFiles);

    if (!this.currentConversation) {
      this.startNewConversation(message, messageFiles);
    } else {
      this.continueConversation(message, messageFiles);
    }

    // remove files
    this.uploadedFiles = [];
  }

  private startNewConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    this.subscriptions.add(
      this.glaiService
        .startConversation(message, messageFiles, this.activeModes)
        .subscribe({
          next: (response) => {
            this.currentConversation = response.item;
            this.glaiService
              .sendToStream(response.item.glai_stream_id, response.glai_payload)
              .finally(() => {
                this.isLoading = false;
              });
            setTimeout(() => this.textareaprompt.nativeElement.focus(), 300);
          },
          error: (error) => {
            this.error = `Failed to start conversation: ${error}`;
            this.isLoading = false;
          },
        })
    );
  }

  private continueConversation(
    message: string,
    messageFiles?: FileDTO[]
  ): void {
    const conversation = this.currentConversation;
    if (!conversation) return;

    this.subscriptions.add(
      this.glaiService
        .continueConversation(
          conversation.id,
          message,
          messageFiles,
          this.activeModes
        )
        .subscribe({
          next: (response) => {
            this.glaiService
              .sendToStream(conversation.glai_stream_id, response.glai_payload)
              .finally(() => {
                this.isLoading = false;
              });
            setTimeout(() => this.textareaprompt.nativeElement.focus(), 300);
          },
          error: (error) => {
            this.error = `Failed to continue conversation: ${error}`;
            this.isLoading = false;
          },
        })
    );
  }

  addToFileList(files: FileDTO): void {
    // add to uploadedFileIds
    this.uploadedFiles.push(files);
  }

  removeFromFileList(file: FileDTO) {
    // remove from uploadedFiles
    this.uploadedFiles = this.uploadedFiles.filter((f) => f.id !== file.id);
  }

  private scrollToBottom(): void {
    this.messagesArea.nativeElement.scrollTo({
      top: this.messagesArea.nativeElement.scrollHeight,
      behavior: 'smooth',
    });
  }

  public resizeInput(): void {
    this.textareaprompt.nativeElement.style.height = 'auto';
    this.textareaprompt.nativeElement.style.height = `${this.textareaprompt.nativeElement.scrollHeight}px`;
    this.scrollToBottom();
  }

  // BEGIN FACILITATOR ONLY FEATURES
  async copyMessage(content: string, $event: Event) {
    const target = $event.target as HTMLElement | null;
    const messageContentEl = target
      ?.closest('.ai-message')
      ?.querySelector('.message-content');

    if (messageContentEl && navigator.clipboard && window.ClipboardItem) {
      const htmlBlob = new Blob([messageContentEl.innerHTML], {
        type: 'text/html',
      });
      const textBlob = new Blob([content], { type: 'text/plain' });
      try {
        await navigator.clipboard.write([
          new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob }),
        ]);
      } catch {
        this.clipboard.copy(content);
      }
    } else {
      this.clipboard.copy(content);
    }

    target?.classList.remove('bi-copy');
    target?.classList.add('bi-check2');
    setTimeout(() => {
      target?.classList.remove('bi-check2');
      target?.classList.add('bi-copy');
    }, 2000);
  }

  onThumbClicked(message: ChatMessage, direction: string): void {
    this.submitUserFeedback(message, direction as AIUserFeedback);
  }

  submitUserFeedback(message: ChatMessage, feedback: AIUserFeedback) {
    if (this.currentConversation) {
      const payload: GlaiUserFeedbackPayload = {
        user_feedback: feedback,
        message_text: message.content,
        role: 'assistant',
      };
      this.subscriptions.add(
        this.glaiService
          .submitUserFeedback(this.currentConversation.id, payload)
          .subscribe({
            next: () => {
              this.feedbackByMessageId[message.id] = feedback;
            },
            error: (error) => {
              this.error = `Failed to submit feedback: ${error}`;
            },
          })
      );
    }
  }

  isThumbsUpSelected(message: ChatMessage): boolean {
    return this.feedbackByMessageId[message.id] === AIUserFeedback.THUMBS_UP;
  }

  isThumbsDownSelected(message: ChatMessage): boolean {
    return this.feedbackByMessageId[message.id] === AIUserFeedback.THUMBS_DOWN;
  }

  openPreviousChatsModal() {
    this.previousChatsModal.open();
  }

  onConversationSelected(payload: SelectedConversationPayload) {
    this.glaiService.disconnectStream();
    this.currentConversation = payload.conversation;
    this.messages = payload.messages;
    this.toggleMode(payload.mode || 'default');
    this.feedbackByMessageId = {};
    setTimeout(() => this.textareaprompt.nativeElement.focus(), 300);
    setTimeout(() => this.scrollToBottom(), 300);
  }

  dynamicPlaceholder(): string {
    const mode = this.activeModes[0] || 'default';
    switch (mode) {
      case 'lesson_and_differentiation':
        return 'Create lessons & activities, get differentiation ideas for student needs.';
      case 'grading_and_feedback':
        return 'Create rubrics, quizzes, and feedback to assess and support student learning.';
      case 'communication':
        return 'Draft professional emails, reports & announcements to copy and edit.';
      case 'classroom_management':
        return 'Get strategies & ideas to improve classroom management & student behavior.';
      case 'data_analysis':
        return 'Analyze data for trends & insights, turn them into clear next steps.';
      case 'leadership':
        return 'What decisions are you trying to make? What are you working on?';
      default:
        return 'Ask anything or get help with GroweLab, from quick questions to guided support.';
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.glaiService.disconnectStream();
  }
}
